import { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { HelpCircle, Info } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  FIGURES_WARMUP_BASE_OPTIONS,
  FIGURES_WARMUP_ADD_OPTIONS,
  FIGURES_TIME_PER_FIGURE_OPTIONS,
  COMPULSORY_DANCE_TIME_OPTIONS,
  TEAMS_WARMUP_OPTIONS,
  WARMUP_ADD_OPTIONS,
} from '@/components/event-timing/constants';

// Which warm-up levels the Figures section shows for each
// classification. "Other" is treated as Local/Regional per owner spec
// — Regionals only. ``local_regional_only`` on any grade (set via the
// Grade Options checkbox above) overrides to Regionals-only.
const LEVELS_BY_CLASSIFICATION = {
  WorldSkate: ['regionals', 'area_state', 'nationals', 'international'],
  Oceania: ['regionals', 'area_state', 'nationals'],
  SkateNZ: ['regionals', 'area_state', 'nationals'],
  Other: ['regionals'],
};

const LEVEL_LABELS = {
  regionals: 'Local/Regional',
  area_state: 'Area/State',
  nationals: 'Nationals',
  international: 'International',
};

const LEVEL_KEYS = {
  regionals: { base: 'warmup_regionals', add: 'warmup_regionals_add' },
  area_state: { base: 'warmup_area_state', add: 'warmup_area_state_add' },
  nationals: { base: 'warmup_nationals', add: 'warmup_nationals_add' },
  international: { base: 'warmup_international', add: 'warmup_international_add' },
};

// Discipline-group resolver — mirrors the backend's
// ``get_event_type_for_discipline`` for the Freeskating/Dance/Teams
// buckets. Figures/Loops share a single Figures-style section.
function resolveTimingShape(disciplineName, roundOneContentType) {
  const d = (disciplineName || '').toLowerCase();
  const ct = (roundOneContentType || '').toLowerCase();
  if (ct === 'compulsories') return 'compulsory-dance';
  if (ct === 'styledance') return 'styledance';
  if (ct === 'freedance') return 'freedance';
  if (d === 'figures' || d === 'loops') return 'figures';
  if (d.includes('freedance')) return 'freedance';
  if (d.includes('styledance')) return 'styledance';
  if (d.includes('dance')) return 'compulsory-dance';
  if (d.includes('freeskating')) return 'freeskating';
  if (['precision', 'quartets', 'duos', 'show'].some(k => d.includes(k))) return 'teams';
  return 'freeskating';
}

export function GradeEventTimingSection({
  gradeId,
  classification,
  localRegionalOnly,
  disciplineName,
  roundOneContentType,
  programmeTime, // read-only reference from the grade form (Round 1 time_min/max)
  onChange, // called with { row, scheduleWide } whenever a field flips
}) {
  const [helpOpen, setHelpOpen] = useState(false);
  const [canShortenHelpOpen, setCanShortenHelpOpen] = useState(false);
  const [row, setRow] = useState({});
  const [scheduleWide, setScheduleWide] = useState({});
  const [loading, setLoading] = useState(true);

  const shape = resolveTimingShape(disciplineName, roundOneContentType);

  // Load existing timing values for this grade (if any) when the
  // dialog opens or the grade changes.
  useEffect(() => {
    if (!gradeId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `${API}/event-timings/by-grade/${gradeId}`,
          { withCredentials: true }
        );
        if (cancelled) return;
        setRow(res.data?.row || {});
        setScheduleWide(res.data?.schedule_wide || {});
      } catch (err) {
        if (!cancelled) {
          setRow({});
          setScheduleWide({});
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [gradeId]);

  const pushUp = (nextRow, nextSched) => {
    if (onChange) {
      onChange({
        row: nextRow !== undefined ? nextRow : row,
        scheduleWide: nextSched !== undefined ? nextSched : scheduleWide,
      });
    }
  };

  const updateRow = (field, value) => {
    const next = { ...row, [field]: value };
    setRow(next);
    pushUp(next, undefined);
  };

  const updateSched = (field, value) => {
    const next = { ...scheduleWide, [field]: value };
    setScheduleWide(next);
    pushUp(undefined, next);
  };

  // Which warm-up level columns are visible for this grade.
  const visibleLevels = (() => {
    if (localRegionalOnly) return ['regionals'];
    return LEVELS_BY_CLASSIFICATION[classification] || LEVELS_BY_CLASSIFICATION.SkateNZ;
  })();

  const renderWarmupPair = (levelKey) => {
    const { base, add } = LEVEL_KEYS[levelKey];
    return (
      <div className="flex items-center gap-1">
        <Select
          value={row[base] || '5:00'}
          onValueChange={(v) => updateRow(base, v)}
        >
          <SelectTrigger className="h-8 text-sm" style={{ width: '85px' }} data-testid={`grade-timing-${base}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="z-[9999]" position="popper" sideOffset={5}>
            {FIGURES_WARMUP_BASE_OPTIONS.map(opt => (
              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-muted-foreground">+</span>
        <Select
          value={row[add] || '2:00'}
          onValueChange={(v) => updateRow(add, v)}
        >
          <SelectTrigger className="h-8 text-sm" style={{ width: '85px' }} data-testid={`grade-timing-${add}`}>
            <SelectValue>{row[add] === 'none' ? '-' : (row[add] || '2:00')}</SelectValue>
          </SelectTrigger>
          <SelectContent className="z-[9999]" position="popper" sideOffset={5}>
            {FIGURES_WARMUP_ADD_OPTIONS.map(opt => (
              <SelectItem key={opt} value={opt}>{opt === 'none' ? '-' : opt}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  };

  const renderFigures = () => (
    <div className="space-y-3">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-stone-100">
            <th className="border px-3 py-2 text-left font-medium" style={{ width: '160px' }}>Schedule</th>
            <th className="border px-3 py-2 text-left font-medium">Warm Up Timing</th>
          </tr>
        </thead>
        <tbody>
          {visibleLevels.map(level => (
            <tr key={level}>
              <td className="border px-3 py-2 bg-white">{LEVEL_LABELS[level]}</td>
              <td className="border px-3 py-2 bg-white">{renderWarmupPair(level)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
        <div>
          <Label className="text-sm">Time per figure</Label>
          <Select
            value={row.time_per_figure || '2:30'}
            onValueChange={(v) => updateRow('time_per_figure', v)}
          >
            <SelectTrigger className="h-8 text-sm mt-1" style={{ width: '110px' }} data-testid="grade-timing-time-per-figure">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="z-[9999]" position="popper" sideOffset={5}>
              {FIGURES_TIME_PER_FIGURE_OPTIONS.map(opt => (
                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end">
          <div className="flex items-center gap-2">
            <Checkbox
              id={`can-shorten-${gradeId}`}
              checked={!!row.can_shorten}
              onCheckedChange={(v) => updateRow('can_shorten', !!v)}
              data-testid="grade-timing-can-shorten"
            />
            <Label htmlFor={`can-shorten-${gradeId}`} className="text-sm font-normal cursor-pointer">
              Can Shorten?
            </Label>
            <button
              type="button"
              onClick={() => setCanShortenHelpOpen(true)}
              className="text-stone-400 hover:text-stone-600 transition-colors"
              title="What does Can Shorten mean?"
              data-testid="can-shorten-help"
            >
              <HelpCircle className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t">
        <div>
          <Label className="text-sm">
            Add for loop action changes (applied if less than 8 skaters)
          </Label>
          <Input
            value={scheduleWide.loop_action_changes || '6:00'}
            onChange={(e) => updateSched('loop_action_changes', e.target.value)}
            placeholder="6:00"
            className="mt-1 h-8"
            data-testid="grade-timing-loop-action"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Applies to all Figures grades on this schedule
          </p>
        </div>
        <div>
          <Label className="text-sm">
            Mid-event warm-ups where there are less than 4 skaters (per figure)
          </Label>
          <Input
            value={scheduleWide.mid_event_warmups || '2:00'}
            onChange={(e) => updateSched('mid_event_warmups', e.target.value)}
            placeholder="2:00"
            className="mt-1 h-8"
            data-testid="grade-timing-mid-event"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Applies to all Figures grades on this schedule
          </p>
        </div>
      </div>
    </div>
  );

  const renderFreeskatingLikeFields = () => (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label className="text-sm">Programme time</Label>
          <Input
            value={programmeTime || '-'}
            disabled
            className="mt-1 h-8 bg-stone-50"
          />
          <p className="text-xs text-muted-foreground mt-1">
            From the Round 1 programme settings
          </p>
        </div>
        <div>
          <Label className="text-sm">Add for warm-up</Label>
          <Select
            value={row.add_for_warmup || '2:00'}
            onValueChange={(v) => updateRow('add_for_warmup', v)}
          >
            <SelectTrigger className="h-8 text-sm mt-1" style={{ width: '100px' }} data-testid="grade-timing-add-warmup">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="z-[9999]" position="popper" sideOffset={5}>
              {WARMUP_ADD_OPTIONS.map(opt => (
                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end">
          <div className="flex items-center gap-2">
            <Checkbox
              id={`can-shorten-${gradeId}`}
              checked={!!row.can_shorten}
              onCheckedChange={(v) => updateRow('can_shorten', !!v)}
              data-testid="grade-timing-can-shorten"
            />
            <Label htmlFor={`can-shorten-${gradeId}`} className="text-sm font-normal cursor-pointer">
              Can Shorten?
            </Label>
            <button
              type="button"
              onClick={() => setCanShortenHelpOpen(true)}
              className="text-stone-400 hover:text-stone-600 transition-colors"
              title="What does Can Shorten mean?"
              data-testid="can-shorten-help"
            >
              <HelpCircle className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t">
        <div>
          <Label className="text-sm">Minutes post-warm-up (first skater)</Label>
          <Input
            value={scheduleWide.minutes_post_warmup_first_skater || '1:00'}
            onChange={(e) => updateSched('minutes_post_warmup_first_skater', e.target.value)}
            placeholder="1:00"
            className="mt-1 h-8"
            data-testid="grade-timing-mins-post-warmup"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Applies to all grades of this type on this schedule
          </p>
        </div>
        <div>
          <Label className="text-sm">Max skaters per warm-up (Cadet/Senior)</Label>
          <Input
            type="number"
            min="1"
            value={scheduleWide.max_skaters_warmup_cadet_senior ?? 5}
            onChange={(e) => updateSched('max_skaters_warmup_cadet_senior', parseInt(e.target.value, 10) || 1)}
            className="mt-1 h-8"
            data-testid="grade-timing-max-cadet-senior"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Applies to all grades of this type on this schedule
          </p>
        </div>
      </div>
    </div>
  );

  const renderCompulsoryDance = () => (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="text-sm">Warm-up per dance</Label>
          <Select
            value={row.warmup_per_dance || '3:00'}
            onValueChange={(v) => updateRow('warmup_per_dance', v)}
          >
            <SelectTrigger className="h-8 text-sm mt-1" style={{ width: '120px' }} data-testid="grade-timing-warmup-per-dance">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="z-[9999]" position="popper" sideOffset={5}>
              {COMPULSORY_DANCE_TIME_OPTIONS.map(opt => (
                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end">
          <div className="flex items-center gap-2">
            <Checkbox
              id={`can-shorten-${gradeId}`}
              checked={!!row.can_shorten}
              onCheckedChange={(v) => updateRow('can_shorten', !!v)}
              data-testid="grade-timing-can-shorten"
            />
            <Label htmlFor={`can-shorten-${gradeId}`} className="text-sm font-normal cursor-pointer">
              Can Shorten?
            </Label>
            <button
              type="button"
              onClick={() => setCanShortenHelpOpen(true)}
              className="text-stone-400 hover:text-stone-600 transition-colors"
              title="What does Can Shorten mean?"
              data-testid="can-shorten-help"
            >
              <HelpCircle className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderTeams = () => (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="text-sm">Warm-up per team</Label>
          <Select
            value={row.warmup_per_team || '0:00'}
            onValueChange={(v) => updateRow('warmup_per_team', v)}
          >
            <SelectTrigger className="h-8 text-sm mt-1" style={{ width: '120px' }} data-testid="grade-timing-warmup-per-team">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="z-[9999]" position="popper" sideOffset={5}>
              {TEAMS_WARMUP_OPTIONS.map(opt => (
                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end">
          <div className="flex items-center gap-2">
            <Checkbox
              id={`can-shorten-${gradeId}`}
              checked={!!row.can_shorten}
              onCheckedChange={(v) => updateRow('can_shorten', !!v)}
              data-testid="grade-timing-can-shorten"
            />
            <Label htmlFor={`can-shorten-${gradeId}`} className="text-sm font-normal cursor-pointer">
              Can Shorten?
            </Label>
            <button
              type="button"
              onClick={() => setCanShortenHelpOpen(true)}
              className="text-stone-400 hover:text-stone-600 transition-colors"
              title="What does Can Shorten mean?"
              data-testid="can-shorten-help"
            >
              <HelpCircle className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderBody = () => {
    if (loading) return <div className="text-sm text-muted-foreground py-2">Loading timings…</div>;
    switch (shape) {
      case 'figures': return renderFigures();
      case 'freeskating':
      case 'freedance':
      case 'styledance':
        return renderFreeskatingLikeFields();
      case 'compulsory-dance': return renderCompulsoryDance();
      case 'teams': return renderTeams();
      default: return renderFreeskatingLikeFields();
    }
  };

  return (
    <div className="mb-4" data-testid="grade-event-timing-section">
      <div className="flex items-center gap-2 mb-2">
        <Label>Event Timing</Label>
        <button
          type="button"
          onClick={() => setHelpOpen(true)}
          className="text-stone-400 hover:text-stone-600 transition-colors"
          title="What do I need for Event Timings"
          data-testid="event-timing-section-help"
        >
          <HelpCircle className="h-4 w-4" />
        </button>
      </div>
      <div className="border rounded-lg p-4 bg-stone-50">
        {renderBody()}
      </div>

      <AlertDialog open={helpOpen} onOpenChange={setHelpOpen}>
        <AlertDialogContent className="max-w-xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Info className="h-5 w-5 text-blue-500" />
              What do I need for Event Timings
            </AlertDialogTitle>
            <AlertDialogDescription className="text-left">
              You can use these fields to set event timings, or you can use a bulk timing form which you can access via the "Event Timing" menu. The grade settings — such as Grade Classification and whether the grade is Local/Regional Only — will determine which timing types you need to set.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction>Okay</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={canShortenHelpOpen} onOpenChange={setCanShortenHelpOpen}>
        <AlertDialogContent className="max-w-xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Info className="h-5 w-5 text-blue-500" />
              What does "Can Shorten" mean?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-left">
              Tick this box if the users can reduce the warmup time when required to assist with programme timings.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction>Okay</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
